import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Loader, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Form } from '@/_shared/components/Form';
import { RenderLocalesWithComponents } from '@/_shared/components/RenderLocalesWithComponents';
import { useNextRechargeInDays } from '@/_shared/hooks/useNextRechargeInDays';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import {
  getOrganizationCreditsCall,
  getOrganizationCreditsQuarterCall,
} from '@/organization/state';
import { ClusterData } from './ClusterData';
import { DivisionByDepartments } from './DivisionByDepartments';
import { DivisionByTrend } from './DivisionByTrend';
import { EmptyState } from './EmptyState';
import { RecapData } from './RecapData';

export const CreditsTab: FC = () => {
  const t = useTranslations('settings.tabs.credits.recap');
  const tShared = useTranslations();
  const { genericResponsiveValue } = useResponsiveStyle();

  const {
    execute: executeGetOrganizationCredits,
    loading: loadingGetOrganizationCredits,
    success: successGetOrganizationCredits,
    error: errorGetOrganizationCredits,
    hasNoData,
  } = getOrganizationCreditsCall.hook();

  const {
    execute: executeGetOrganizationCreditsQuarter,
    loading: loadingGetOrganizationCreditsQuarter,
    error: errorGetOrganizationCreditsQuarter,
    defaultQuarter,
    response: responseGetOrganizationCreditsQuarter,
  } = getOrganizationCreditsQuarterCall.hook();

  const { control, watch } = useForm({
    defaultValues: {
      filterQuarter: {
        value: defaultQuarter || '',
      },
    },
  });

  const fieldValues = watch();

  const { days, nextMonth } = useNextRechargeInDays();

  useEffect(() => {
    executeGetOrganizationCreditsQuarter({
      successSaga: ({ response }) => {
        if (response.length === 0) {
          return;
        }
        executeGetOrganizationCredits({
          year: response[0].year,
          quarter: response[0].quarter,
        });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fieldValues.filterQuarter) {
      const [quarter, year] = fieldValues.filterQuarter.value.split('-');
      if (quarter !== 'undefined' && year !== 'undefined') {
        executeGetOrganizationCredits({
          year: Number(year),
          quarter: Number(quarter),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValues.filterQuarter]);

  if (loadingGetOrganizationCredits || loadingGetOrganizationCreditsQuarter) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader color='primary' size='s' />
      </Box>
    );
  }

  if (errorGetOrganizationCreditsQuarter || responseGetOrganizationCreditsQuarter?.length === 0) {
    return <EmptyState />;
  }

  if (errorGetOrganizationCredits || errorGetOrganizationCreditsQuarter) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (
    successGetOrganizationCredits &&
    !loadingGetOrganizationCredits &&
    !errorGetOrganizationCredits
  ) {
    return (
      <Box>
        {hasNoData ? (
          <EmptyState />
        ) : (
          <Box>
            <Box
              mt='m'
              mb='s'
              display='flex'
              justifyContent='space-between'
              flexDirection={genericResponsiveValue({
                breakPoints: { desktop: 'row' },
                defaultValue: 'column',
              })}
              alignItems={genericResponsiveValue({
                breakPoints: { desktop: 'center' },
                defaultValue: 'flex-start',
              })}
            >
              <Typography
                variant='h1'
                mb={genericResponsiveValue({
                  breakPoints: { desktop: 'none' },
                  defaultValue: 'xxs',
                })}
              >
                {t('title')}
              </Typography>
              <Typography variant='p2/light'>
                <RenderLocalesWithComponents
                  text={t('nextRecharge', {
                    date: '!{DATE}!',
                    days: days.toString(),
                  })}
                  placeholders={{
                    '!{DATE}!': (
                      <Typography as='span' variant='p2/light' fontWeight='500'>
                        {nextMonth}
                      </Typography>
                    ),
                  }}
                />
              </Typography>
            </Box>
            <Box>
              <Typography variant='p3/light'>{t('description')}</Typography>
            </Box>
            <Box width='300px' mt='s' mb='m'>
              {responseGetOrganizationCreditsQuarter && (
                <Form.FormDropdown
                  // @ts-ignore
                  control={control}
                  name='filterQuarter'
                  items={
                    responseGetOrganizationCreditsQuarter
                      ? responseGetOrganizationCreditsQuarter.map(item => ({
                          label: `Q${item.quarter} - ${item.year}`,
                          value: `${item.quarter}-${item.year}`,
                        }))
                      : []
                  }
                  labelStyle={{ color: 'white', mb: 'xxs' }}
                  data-testid='dropdown-select-campaign'
                  optionTestId='dropdown-select-campaign-option'
                  placeholder={
                    `Q${responseGetOrganizationCreditsQuarter?.[0].quarter} - ${responseGetOrganizationCreditsQuarter?.[0].year}` ||
                    ''
                  }
                />
              )}
            </Box>
            <RecapData />
            <ClusterData />
            <DivisionByDepartments />
            <DivisionByTrend />
          </Box>
        )}
      </Box>
    );
  }

  return null;
};
